/* eslint-disable sort-exports/sort-exports */

/* eslint-disable typescript-sort-keys/string-enum */
import type { HotKeys } from '#~/hot-keys/index.js';
import type { ChildrenOnly } from '@studio/design-system/props';
import type { Account } from '@studio/models/account';
import type { Organization } from '@studio/models/organization';
import type { Room } from '@studio/models/room';
import type { User } from '@studio/models/user';
import type * as S from '@studio/react-utils/store';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { UseBoundStoreWithEqualityFn } from 'zustand/traditional';

/** Workflow panels. */
export enum Panel {
  Billing = 'b',
  Controllers = 'c',
  Profile = 'pr',
  Organization = 'o',
  Studio = 's',
  StudioUsers = 'su',
  Notifications = 'n',
  Password = 'pwd',
  HotKeys = 'hk',
  Pricing = 'pl',
  Users = 'u'
}

/** Top level panels. */
export enum TopLevelPanel {
  Account = 'ta',
  Studio = 'ts',
  Organization = 'to'
}

/** Mutable data draft. */

/**
 *@fixme - Missing JSDoc
 */
export type Draft = S.Draft<State>;

/** Immer producer. */
export type Producer = S.Producer<State>;

/** `<Provider>` properties. */
export type Props = ChildrenOnly & {
  account: Account;
  defaultPanel: T_Maybe<Panel>;
  defaultTopLevelPanel: T_Maybe<TopLevelPanel>;
  initialPlanPickerRoomId?: Room['id'] | undefined;
  hotKeys: HotKeys;
  organizations: Record<Organization['id'], Organization>;
  overrideOrganizationId: T_Maybe<Organization['id']>;
  overrideRoomId: T_Maybe<Room['id']>;

  rooms: Record<Room['id'], Room>;
  user: User;
};

/** Store state. */
export type State = Readonly<{
  /** `Panel.Billing` panel state. */
  [Panel.Billing]: { initialPlanPickerRoomId?: Room['id'] | undefined };

  /** `Panel.Controllers` panel state. */
  [Panel.Controllers]: object;

  /** `Panel.HotKeys` panel state. */
  [Panel.HotKeys]: { hotKeys: HotKeys; isDefault: boolean; isDirty: boolean; snapshot: HotKeys };

  /** `Panel.Notifications` panel state. */
  [Panel.Notifications]: { isDirty: boolean };

  /** `Panel.Password` state. */
  [Panel.Password]: { isDirty: boolean };

  /** `Panel.Pricing` panel state. */
  [Panel.Pricing]: { isDirty: boolean };

  /** `Panel.Profile` panel state. */
  [Panel.Profile]: { isDirty: boolean; nextUser: User };

  /** `Panel.Studio` panel state. */
  [Panel.Studio]: { isDirty: boolean; overrideId: T_Maybe<Room['id']> };

  /** `Panel.Organization` panel state. */
  [Panel.Organization]: { isDirty: boolean; overrideId: T_Maybe<Organization['id']> };

  /** Selected panel. */
  panel: Panel;

  /** Immer producer. */
  produce: Producer;

  /** Selected top level panel. */
  topLevelPanel: TopLevelPanel;
}>;

/** Workflow store. */
export type Store = UseBoundStoreWithEqualityFn<S.Store<State>>;
